import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CCard, CCardBody, CCardHeader, CTable, CTableHead, CTableRow, CTableHeaderCell,
  CTableBody, CTableDataCell, CFormInput, CButton, CSpinner, CPagination, CPaginationItem,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSearch } from '@coreui/icons';
import api from '../../services/api';
import { downloadPdf } from '../../services/financeDownload';

const PAGE_SIZE = 30;

export default function PaymentHistoryPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get('/finance/payments', { params: { page, search: q || undefined } })
      .then((r) => {
        if (cancelled) return;
        setRows(r.data.payments || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, q]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function doSearch() {
    setPage(1);
    setQ(search.trim());
  }

  async function onPdf(pid) {
    try {
      await downloadPdf(`/finance/payments/${pid}/pdf`, `PAY-${pid}.pdf`);
    } catch {
      /* ignore */
    }
  }

  return (
    <>
      <h5 className="mb-3">Payment history</h5>
      <CCard className="border-0 shadow-sm mb-3" style={{ borderRadius: 12 }}>
        <CCardHeader className="bg-white d-flex flex-wrap gap-2 align-items-center py-3">
          <CFormInput
            placeholder="Filter invoice, job, receipt ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 280 }}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), doSearch())}
          />
          <CButton color="success" variant="outline" size="sm" onClick={doSearch}>
            <CIcon icon={cilSearch} className="me-1" /> Search
          </CButton>
        </CCardHeader>
        <CCardBody className="p-0">
          {loading ? (
            <div className="text-center py-5"><CSpinner color="success" /></div>
          ) : (
            <CTable hover responsive className="mb-0" style={{ fontSize: 14 }}>
              <CTableHead color="light">
                <CTableRow>
                  <CTableHeaderCell>Receipt</CTableHeaderCell>
                  <CTableHeaderCell>Date</CTableHeaderCell>
                  <CTableHeaderCell>Invoice</CTableHeaderCell>
                  <CTableHeaderCell>Job</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">Amount</CTableHeaderCell>
                  <CTableHeaderCell>By</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">Actions</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {rows.length === 0 && (
                  <CTableRow>
                    <CTableDataCell colSpan={7} className="text-center text-muted py-4">No payments yet.</CTableDataCell>
                  </CTableRow>
                )}
                {rows.map((p) => (
                  <CTableRow key={p.id}>
                    <CTableDataCell>PAY-{p.id}</CTableDataCell>
                    <CTableDataCell>{String(p.paid_at || '').replace('T', ' ').slice(0, 19)}</CTableDataCell>
                    <CTableDataCell>
                      <Link to={`/finance/invoices/${p.invoice_id}`}>{p.invoice_no}</Link>
                    </CTableDataCell>
                    <CTableDataCell>{p.jobcode} <span className="text-muted">(#{p.job_jid})</span></CTableDataCell>
                    <CTableDataCell className="text-end">RM {Number(p.amount).toFixed(2)}</CTableDataCell>
                    <CTableDataCell>{p.recorded_by}</CTableDataCell>
                    <CTableDataCell className="text-end text-nowrap">
                      <CButton component={Link} to={`/finance/payments/${p.id}/receipt`} color="link" size="sm" className="p-0 me-2">HTML</CButton>
                      <CButton color="link" size="sm" className="p-0" onClick={() => onPdf(p.id)}>PDF</CButton>
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          )}
        </CCardBody>
      </CCard>

      {pages > 1 && (
        <CPagination align="center" aria-label="Payments pagination">
          <CPaginationItem disabled={page <= 1} style={{ cursor: page > 1 ? 'pointer' : 'default' }} onClick={() => page > 1 && setPage(page - 1)}>Prev</CPaginationItem>
          <CPaginationItem active>{page}</CPaginationItem>
          <CPaginationItem disabled={page >= pages} style={{ cursor: page < pages ? 'pointer' : 'default' }} onClick={() => page < pages && setPage(page + 1)}>Next</CPaginationItem>
        </CPagination>
      )}
    </>
  );
}
